import { useEffect, useRef, useCallback } from 'react';

/**
 * A hook that traps focus within a container element when active.
 * Handles Tab/Shift+Tab cycling and Escape key to close.
 * Returns focus to the trigger element when deactivated.
 */
export function useFocusTrap(isActive: boolean, onEscape?: () => void) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Store the trigger element when trap activates
  useEffect(() => {
    if (isActive) {
      triggerRef.current = document.activeElement as HTMLElement;
    }
  }, [isActive]);

  // Return focus to trigger when trap deactivates
  useEffect(() => {
    if (!isActive && triggerRef.current) {
      // Small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        triggerRef.current?.focus();
        triggerRef.current = null;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    
    const focusableSelectors = [
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'a[href]:not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
    ].join(', ');

    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter(el => el.offsetParent !== null); // Filter out hidden elements
  }, []);

  // Handle keyboard events for focus trapping
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Escape key
      if (e.key === 'Escape' && onEscape) {
        e.preventDefault();
        onEscape();
        return;
      }

      // Handle Tab key for focus trapping
      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Shift+Tab on first element -> go to last
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
        // Tab on last element -> go to first
        else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
        // If focus is outside the container, bring it back
        else if (
          containerRef.current &&
          !containerRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, onEscape, getFocusableElements]);

  // Focus the first focusable element when trap activates
  useEffect(() => {
    if (isActive && containerRef.current) {
      // Small delay to ensure the container is rendered
      const timer = setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isActive, getFocusableElements]);

  return { containerRef };
}
